import _ from 'lodash';
import React from 'react';
import { getPrecisePriceWithCurrency, getPriceWithDisplayCurrencyByPrecision, getFreelancerExchangeRate } from 'arbitrip-common/general/utils/Currencies';
import {
    getPriceValue,
    getBookingPricingBreakdown,
    getAggregatedPricingBreakdown,
    formatAmountsByCurrency,
    getAggregatedCommissionPercentageText,
} from './utils';
import helpers from './helpers';
import { useTranslation } from 'react-i18next';

const SettlePaymentSummary = ({ booking, bookings, isMultiMode, showEditCommission, onEditCommission, chargeCurrency, profile }) => {
    const { t } = useTranslation();
    const isAggregate = isMultiMode && Array.isArray(bookings) && bookings.length > 1;

    if (isAggregate) {
        const { currencies, totalsByCurrency, unpriceableBookings } = getAggregatedPricingBreakdown(bookings);

        const commissionPercentageText = getAggregatedCommissionPercentageText(totalsByCurrency, currencies);
        const pointsCurrencies = currencies.filter((currency) => totalsByCurrency[currency].points_in_currency > 0);

        return (
            <div className="change-client-price">
                <div className="pricing-summary">
                    <label>{t('Bookings.SettlePayment.PricingSummary')}</label>
                    {unpriceableBookings.length > 0 && (
                        <div className="pricing-summary__unpriceable-warning">
                            {t('Bookings.SettlePayment.UnpriceableBookingsExcluded', { count: unpriceableBookings.length })}
                        </div>
                    )}
                    <div className="pricing-item">
                        <div className="price-title">{t('Bookings.SettlePayment.TotalNetPrice')}</div>
                        <div className="price-value">{formatAmountsByCurrency(totalsByCurrency, currencies, 'total_net_price')}</div>
                    </div>
                    <div className="pricing-item">
                        <div className="price-title">{t('Bookings.SettlePayment.Commission')}</div>
                        <div className="price-value">{commissionPercentageText}</div>
                    </div>
                    <div className="pricing-item">
                        <div className="price-title">{t('Bookings.SettlePayment.TotalCommission')}</div>
                        <div className="price-value">{formatAmountsByCurrency(totalsByCurrency, currencies, 'total_commission')}</div>
                    </div>
                    <div className="pricing-item bold">
                        <div className="price-title">{t('Bookings.SettlePayment.TotalClientPrice')}</div>
                        <div className="price-value">{formatAmountsByCurrency(totalsByCurrency, currencies, 'total_client_price')}</div>
                    </div>
                    {pointsCurrencies.length > 0 && (
                        <div className="pricing-item">
                            <div className="price-title">{t('Bookings.SettlePayment.PointsDiscount')}</div>
                            <div className="price-value">
                                {pointsCurrencies
                                    .map((currency) =>
                                        getPrecisePriceWithCurrency(-totalsByCurrency[currency].points_in_currency, currency),
                                    )
                                    .join(' + ')}
                            </div>
                        </div>
                    )}
                    <hr />

                    <div className="pricing-item bold sum">
                        <div className="price-title">{t('Bookings.SettlePayment.TotalToPay')}</div>
                        <div className="price-value">{formatAmountsByCurrency(totalsByCurrency, currencies, 'total_to_pay')}</div>
                    </div>
                </div>
            </div>
        );
    }

    const {
        total_net_price,
        total_client_price,
        total_commission,
        currency,
        points_in_currency,
        total_to_pay,
        isPriceable,
    } = getBookingPricingBreakdown(booking);

    const commission_percentage = helpers.getCommissionPercentage(booking);

    const additional_currency = chargeCurrency && chargeCurrency !== currency;

    return (
        <div className="change-client-price">
            <div className="pricing-summary">
                <label>{t('Bookings.SettlePayment.PricingSummary')}</label>
                {!isPriceable && (
                    <div className="pricing-summary__unpriceable-warning">
                        {t('Bookings.SettlePayment.UnpriceableBookingsExcluded', { count: 1 })}
                    </div>
                )}
                <div className="pricing-item">
                    <div className="price-title">{t('Bookings.SettlePayment.TotalNetPrice')}</div>
                    {getPriceValue(total_net_price, currency, chargeCurrency, profile)}
                </div>
                <div className="pricing-item">
                    <div className="price-title">{t('Bookings.SettlePayment.Commission')}</div>
                    <div className="price-value">
                        {showEditCommission && _.isFunction(onEditCommission) && (
                            <span className="edit-commission" onClick={onEditCommission}>
                                {t('Bookings.SettlePayment.EditCommission')}
                            </span>
                        )}
                        {commission_percentage}%
                    </div>
                </div>
                <div className="pricing-item">
                    <div className="price-title">{t('Bookings.SettlePayment.TotalCommission')}</div>
                    {getPriceValue(total_commission, currency, chargeCurrency, profile)}
                </div>
                <div className="pricing-item bold">
                    <div className="price-title">{t('Bookings.SettlePayment.TotalClientPrice')}</div>
                    {getPriceValue(total_client_price, currency, chargeCurrency, profile)}
                </div>
                {points_in_currency > 0 && (
                    <div className="pricing-item">
                        <div className="price-title">{t('Bookings.SettlePayment.PointsDiscount')}</div>
                        <div className="price-value">{getPrecisePriceWithCurrency(-points_in_currency, currency)}</div>
                    </div>
                )}
                <hr />

                <div className="pricing-item bold sum">
                    <div className="price-title">{t('Bookings.SettlePayment.TotalToPay')}</div>
                    <div className="price-value">
                        {!isPriceable ? (
                            '—'
                        ) : additional_currency ? (
                            <React.Fragment>
                                {getPriceWithDisplayCurrencyByPrecision(
                                    total_to_pay,
                                    getFreelancerExchangeRate(profile, currency, chargeCurrency),
                                    chargeCurrency,
                                    2,
                                )}
                            </React.Fragment>
                        ) : (
                            getPrecisePriceWithCurrency(total_to_pay, currency)
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettlePaymentSummary;
